import { useState } from "react"
import { Link } from "@tanstack/react-router"
import { ShoppingCart, Store, ArrowRight, CheckCircle2 } from "lucide-react"
import { motion, useMotionTemplate, useMotionValue } from "framer-motion"

interface RoleCardProps {
  icon: typeof ShoppingCart
  title: string
  subtitle: string
  desc: string
  features: string[]
  linkText: string
  to: "/signup"
  search: { role: "buyer" | "seller" }
}

function SpotlightCard({ icon: IconComponent, title, subtitle, desc, features, linkText, to, search }: RoleCardProps) {
  const mouseX = useMotionValue(0)
  const mouseY = useMotionValue(0)

  function handleMouseMove({ currentTarget, clientX, clientY }: React.MouseEvent) {
    const { left, top } = currentTarget.getBoundingClientRect()
    mouseX.set(clientX - left)
    mouseY.set(clientY - top)
  }

  return (
    <motion.div
      onMouseMove={handleMouseMove}
      whileHover={{ y: -6 }}
      transition={{ type: "spring", stiffness: 200, damping: 20 }}
      className="group relative rounded-2xl bg-white border border-outline-variant/70 p-5 md:p-6 flex flex-col justify-between shadow-sm hover:shadow-xl hover:shadow-primary/5 transition-all duration-300 overflow-hidden"
    >
      {/* Dynamic Cursor Spotlight Radial Glow */}
      <motion.div
        className="pointer-events-none absolute -inset-px rounded-2xl opacity-0 transition-opacity duration-300 group-hover:opacity-100"
        style={{
          background: useMotionTemplate`
            radial-gradient(
              400px circle at ${mouseX}px ${mouseY}px,
              rgba(190, 0, 30, 0.07),
              transparent 80%
            )
          `,
        }}
      />

      <div className="relative z-10">
        {/* Header with Icon */}
        <div className="flex items-center justify-between mb-4">
          <div className="size-10 rounded-xl bg-surface-container flex items-center justify-center text-primary border border-outline-variant/40 group-hover:bg-primary group-hover:text-white transition-all duration-300 shadow-xs">
            <IconComponent className="size-5 transition-transform duration-300 group-hover:scale-110" />
          </div>
          <span className="text-[10px] font-bold uppercase tracking-widest text-primary font-mono px-2 py-0.5 rounded-full bg-primary/5 border border-primary/15">
            {subtitle}
          </span>
        </div>

        <h3 className="font-sans text-xl md:text-2xl font-black text-on-surface mb-1.5 tracking-tight">
          {title}
        </h3>
        
        <p className="text-secondary text-xs md:text-sm leading-relaxed mb-4 font-normal">
          {desc}
        </p>

        {/* Feature Tags / Badges */}
        <div className="space-y-2 mb-5">
          {features.map((feat, idx) => (
            <div key={idx} className="flex items-center gap-2 text-xs font-semibold text-on-surface/90">
              <CheckCircle2 className="size-3.5 text-primary shrink-0" />
              <span>{feat}</span>
            </div>
          ))}
        </div>
      </div>
      
      <div className="relative z-10 pt-3 border-t border-outline-variant/40">
        <Link
          to={to}
          search={search}
          className="inline-flex items-center gap-2 text-primary hover:text-[#a5001a] text-sm font-bold tracking-wide uppercase group/link transition-colors"
        >
          <span>{linkText}</span>
          <ArrowRight className="size-4 transition-transform duration-200 group-hover/link:translate-x-1.5" />
        </Link>
      </div>
    </motion.div>
  )
}

export function RoleCards() {
  const roles: RoleCardProps[] = [
    {
      icon: ShoppingCart,
      title: "For Enterprise Buyers",
      subtitle: "Procurement Teams",
      desc: "Access an audited network of global manufacturers, compare automated line-item quotations, and protect your capital with escrow.",
      features: [
        "Instant AI RFQ Parsing & Market Depth",
        "GST & Physical Factory Audit Verification",
        "Dispute-Free Milestone Escrow Settlement",
        "Integrated Cross-Border Container Tracking"
      ],
      linkText: "Register as Buyer",
      to: "/signup",
      search: { role: "buyer" },
    },
    {
      icon: Store,
      title: "For Verified Suppliers",
      subtitle: "Mills & Manufacturers",
      desc: "Showcase manufacturing capabilities directly to enterprise buyers worldwide, quote on live active RFQs, and secure guaranteed payouts.",
      features: [
        "Direct Access to Qualified Global RFQ Leads",
        "Structured Bidding with Transparent Terms",
        "Guaranteed Payment Release upon Dispatch",
        "Zero Cold Outreach or Marketing Overhead"
      ],
      linkText: "Register as Seller",
      to: "/signup",
      search: { role: "seller" },
    },
  ]

  return (
    <section id="role-cards" className="relative py-12 md:py-16 overflow-hidden bg-surface-container-low/60 border-t border-outline-variant/40">
      
      {/* Background ambient lighting */}
      <div className="absolute right-[-10%] top-[-10%] w-[400px] h-[400px] rounded-full bg-primary/5 blur-[100px] pointer-events-none" />
      <div className="absolute left-[-5%] bottom-[-10%] w-[400px] h-[400px] rounded-full bg-blue-500/5 blur-[100px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        
        {/* Section Header */}
        <div className="mb-10 space-y-3 text-center max-w-2xl mx-auto">
          <span className="inline-flex items-center gap-1.5 px-3 py-0.5 rounded-full text-xs font-black bg-primary/10 text-primary border border-primary/20 uppercase tracking-widest">
            Select Your Workspace
          </span>
          <h2 className="font-sans text-2xl md:text-3xl lg:text-4xl font-black text-on-surface tracking-tight">
            Tailored For Your Role
          </h2>
          <p className="text-secondary text-xs md:text-sm font-normal leading-relaxed">
            Join the ecosystem with dedicated, customized portals built specifically for enterprise procurement teams and certified manufacturers.
          </p>
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 max-w-4xl mx-auto">
          {roles.map((role, i) => (
            <SpotlightCard key={i} {...role} />
          ))}
        </div>
      </div>
    </section>
  )
}

